// ─── API contract ────────────────────────────────────────────────────────────
// What an insight is drawn as. The overview renders the types it has a card for and skips the rest, so a type
// the CMS adds before the frontend knows it never breaks the page.
export const INSIGHT_TYPES = { TABLE: 'table', BAR: 'bar', PIE: 'pie', LOCKED: 'locked' }

// How a column's values print (`columns[].format`).
export const INSIGHT_COLUMN_FORMATS = {
  TEXT: 'text', NUMBER: 'number', PERCENT: 'percent', RANGE: 'range', SPARKLINE: 'sparkline',
}

// The column every ranked table leads with; the card adds it when the CMS leaves it out.
export const INSIGHT_RANK_KEY = 'rank'

// The row's sport, which the API sends as a column but is never drawn as one: the groupings listed here print
// it above the row's label instead (a set name alone doesn't say which sport it is).
export const INSIGHT_SPORT_KEY = 'sport'
export const INSIGHT_SPORT_LABEL_GROUPS = ['set']

// Cards the overview draws before it knows how many insights it has.
export const INSIGHTS_OVERVIEW_PLACEHOLDERS = 6

// The tab bars the report's skeleton draws before it knows its tabs: one per Top Selling tab, about as wide as
// its label.
export const INSIGHTS_REPORT_TAB_PLACEHOLDERS = ['w-12', 'w-11', 'w-8', 'w-9', 'w-11', 'w-10', 'w-24', 'w-20']

// ─── Period filter ───────────────────────────────────────────────────────────
export const INSIGHTS_PERIODS = [
  { value: 'last_7_days',  label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'last_year',    label: 'Last year' },
]

export const DEFAULT_INSIGHTS_PERIOD = 'last_90_days'

// ─── Report table ────────────────────────────────────────────────────────────
// Reports open ranked by units sold, most first.
export const DEFAULT_INSIGHTS_SORT = { key: 'sold', direction: 'desc' }

// One report page. The server pages, sorts and searches (`page` / `per_page` / `sort_by` / `sort_dir` / `q`), so
// this is what one request asks for, not a slice of rows already loaded.
export const INSIGHTS_REPORT_PAGE_SIZE = 10

// How long the report waits after the last keystroke before searching — each search is a request.
export const INSIGHTS_SEARCH_DEBOUNCE_MS = 300

// The room (the content column's width) the report table needs to show its Trend column; with less, Trend is
// the first column to go. The table without it fits 782px, and narrower than that it scrolls sideways in its card.
export const INSIGHTS_REPORT_TREND_MIN_WIDTH = 944

// ─── Top-selling cards ───────────────────────────────────────────────────────
// Rows one overview card lists, under its All row, when the insight doesn't say (`card_row_limit`). The
// skeleton draws as many, so the card doesn't change height when the rows land.
export const INSIGHTS_CARD_ROWS = 5

// Rows the Home page's preview of a card lists under its All row: the design's three.
export const INSIGHTS_HOME_CARD_ROWS = 3

// Cards Home's Insights row shows at most, in the CMS's order.
export const INSIGHTS_HOME_CARD_LIMIT = 5

// Rows of a report the cheapest plan can read; the rest sit behind the upgrade overlay.
export const INSIGHTS_FREE_ROWS = 5

// Blurred placeholder rows a gated report draws under its real ones. The server never sends the hidden rows
// (`hidden_row_count` says how many there are), so these are made up on the client and only ever seen blurred.
export const INSIGHTS_GATED_PLACEHOLDER_ROWS = 5

// What opened the Insights upgrade dialog, for the paywall events: the upsell panel's button, or a locked
// sort or search.
export const INSIGHTS_UPGRADE_TRIGGERS = { CTA: 'cta', SORT: 'sort', SEARCH: 'search' }

// Analytics values the Insights funnel is filtered on in PostHog: `source` on `insights_upgrade_clicked`, and
// `source_page` on `subscription_upgrade_initiated` when the checkout started from Insights.
export const INSIGHTS_ANALYTICS_SOURCE = 'insights'
export const INSIGHTS_UPGRADE_SOURCE_PAGE = 'Insights'

// `entry_point` on `insights_viewed`: how the dealer reached the report — from the Insights overview (where the
// sidebar leads), from the Insights block on Home, or straight to its URL (typed, reloaded, linked from outside).
export const INSIGHTS_ENTRY_POINTS = { NAV: 'nav', HOME_CARD: 'home_card', DEEP_LINK: 'deep_link' }

// Trend sparkline strokes: green-400 when the window ends higher than it began, red-500 otherwise.
export const INSIGHTS_TREND_COLORS = { up: '#31C48D', down: '#F05252' }

// Sell-through badge tints, against the CDP median the API sends (`meta.sell_through_scale`): green above it, red
// below, plain at it. Emerald-500 and rose-500 are the design's green and red; how much of them shows is set per
// badge (`--tw-bg-opacity`), from none at the median to the scale's `max_opacity` at its ends.
export const INSIGHTS_SELL_THROUGH_TINTS = {
  above: 'bg-emerald-500 text-emerald-900',
  below: 'bg-rose-500 text-rose-900',
  none:  'text-gray-900',
}

// ─── Inventory links ─────────────────────────────────────────────────────────
// The report cells (by `field_key`) that open the dealer's inventory for their row, with the query each adds
// to the row's filter: the label keeps every status; the You group's Sold and Available narrow to sold and
// on-hand cards.
export const INSIGHTS_INVENTORY_LINKS = {
  label:         {},
  you_sold:      { status: 'sold_out' },
  you_available: { status: 'available' },
}

// The Inventory query key a row's label filters by, per what the insight groups by (`group_by_column`, a
// value the backend owns). Players have no player filter, so they use the search box; prices send a range.
// A grouping missing here (serial numbers) has no Inventory filter yet, so its cells stay text.
export const INSIGHT_INVENTORY_FILTERS = {
  player: 'q',
  team:   'team',
  set:    'set',
  year:   'year',
  sport:  'category',
  price:  'price',
}

// The data source of the price-bucket insight, which has no `group_by_column` — its rows ARE the grouping.
export const INSIGHT_PRICES_DATA_SOURCE = 'top_selling.prices'
